/**
 * BookingActions — the right-rail "Actions" card on the booking detail view.
 *
 * Both rows follow the disabled-CTA pattern: they are ALWAYS present, disabled
 * with a hint when they cannot be used, so a CSR learns that the action exists
 * and why it will not go rather than hunting for a control that was never
 * rendered. The hint is the most specific true reason — see `rebookAffordance`
 * and `refundAffordance`, which own that ordering so the two surfaces cannot
 * drift.
 *
 * Rebook (BON-146) is gated on status, `rebook:bookings`, and the cutoff: past
 * 10 AM venue-local on the day of the workshop it disables, UNLESS the caller
 * holds `override:rebook-cutoff`, who still gets in and confirms the override
 * explicitly inside the drawer. There is deliberately no second "rebook outside
 * cutoff window" entry point — one button, and the drawer carries the consent.
 *
 * The second row RETIRES the booking: a comp is cancelled, a paid one refunded.
 * They are mutually exclusive, so it is one row that changes label rather than
 * two with one always disabled. `retireAffordance` owns the branch.
 */
import { CalendarClock, ChevronRight, Mail, RotateCcw, XCircle } from 'lucide-react';

import type { BookingStatus } from '../../api/bookingsV2';

import {
  rebookAffordance,
  resendConfirmationAffordance,
  retireAffordance,
} from './bookingDetailModel';
import { useGiftPassesEnabled } from './use-gift-passes-enabled';

export interface BookingActionsProps {
  status: BookingStatus;
  /** Result of usePermissions().hasAnyPermission('rebook:bookings'). */
  canRebook: boolean;
  /** Result of usePermissions().hasPermission('override:rebook-cutoff'). Lifts
   *  the cutoff gate below; the explicit opt-in still lives in the drawer. */
  canOverrideCutoff: boolean;
  /** Server verdict — 10 AM venue-local on the day of the workshop has passed. */
  cutoffPassed: boolean;
  /** Result of usePermissions().hasAnyPermission('refund:bookings') — the route
   *  enforces that one capability for a cancel too. */
  canRetire: boolean;
  /** Decides which job the retire row does. */
  isComp: boolean;
  /** The booking's net cash (`money.amountPaidCents`) — a refund returns cash,
   *  so a comp or an import with none is refusable regardless of permission. */
  amountPaidCents: number | null;
  /** Absolute instant the workshop ends; past it neither path is allowed. */
  workshopEndsAt: string;
  /** Result of usePermissions().hasAnyPermission('write:bookings') (BON-630). */
  canResendConfirmation: boolean;
  onRebook: () => void;
  onRetire: () => void;
  onResendConfirmation: () => void;
}

const cardCls =
  'bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700';

// A plain (lowercase) function, not a component: `react/no-multi-comp` allows
// exactly one component per file, and this card's only export is the whole
// `BookingActions` card, not its individual rows.
function actionRow({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
  disabledHint,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled: boolean;
  disabledHint: string;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={title}
      title={disabled ? disabledHint : undefined}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition ${
        disabled
          ? 'border-stone-200 dark:border-stone-700 opacity-50 cursor-not-allowed'
          : 'border-stone-200 dark:border-stone-700 hover:border-moss-400 hover:bg-moss-50/50 dark:hover:bg-moss-900/10'
      }`}
    >
      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-stone-100 dark:bg-stone-700/50 flex items-center justify-center text-stone-500 dark:text-stone-300">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-bark-900 dark:text-stone-100">
          {title}
        </span>
        <span className="block text-xs text-stone-500 dark:text-stone-400">{subtitle}</span>
      </span>
      <ChevronRight size={16} className="flex-shrink-0 text-stone-400" />
    </button>
  );
}

export function BookingActions({
  status,
  canRebook,
  canOverrideCutoff,
  cutoffPassed,
  canRetire,
  isComp,
  amountPaidCents,
  workshopEndsAt,
  canResendConfirmation,
  onRebook,
  onRetire,
  onResendConfirmation,
}: BookingActionsProps) {
  const giftPassesEnabled = useGiftPassesEnabled();
  const rebook = rebookAffordance(status, canRebook, cutoffPassed, canOverrideCutoff);
  const retire = retireAffordance(
    status,
    isComp,
    amountPaidCents,
    workshopEndsAt,
    canRetire,
    giftPassesEnabled,
  );
  const resend = resendConfirmationAffordance(status, canResendConfirmation);

  return (
    <div className={cardCls}>
      <div className="px-5 py-4 border-b border-stone-200 dark:border-stone-700">
        <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100">Actions</h2>
      </div>
      <div className="p-4 space-y-2.5">
        {actionRow({
          icon: <CalendarClock size={18} />,
          title: 'Rebook',
          subtitle: 'Move to another workshop',
          onClick: onRebook,
          disabled: !rebook.enabled,
          disabledHint: rebook.hint,
        })}
        {actionRow({
          icon: retire.kind === 'cancel' ? <XCircle size={18} /> : <RotateCcw size={18} />,
          title: retire.kind === 'cancel' ? 'Cancel booking' : 'Refund',
          subtitle:
            retire.kind === 'cancel'
              ? 'Release the seats — no money moves'
              : "Return the customer's money",
          onClick: onRetire,
          disabled: !retire.enabled,
          disabledHint: retire.hint,
        })}
        {actionRow({
          icon: <Mail size={18} />,
          title: 'Resend confirmation',
          subtitle: 'Email the confirmation again',
          onClick: onResendConfirmation,
          disabled: !resend.enabled,
          disabledHint: resend.hint,
        })}
      </div>
    </div>
  );
}
